import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';

import { IcBackIcon, IcCloseSIcon, IcEditIcon, IcDeleteIcon, IcArrowDown16Icon } from '@gem/icons';
import { IconButton, IconViewSizes, customScrollStyles } from '@gem/ui-kit';

import {
  ICalendarAuthAccount,
  ConfirmationType,
  IEvent,
  IUpdateCalendarEventParams,
  IUpdateRecurrenceCalendarEventParams,
} from 'APP/model/calendar/calendar.types';

import { ActiveSidebarView, IEventCalendar, IUpdateEvent } from '../Content.types';
import { Create } from './Create/Create';
import { Edit } from './Edit/Edit';
import { Info } from './Info/Info';
import { useRightSidebarPresenter } from './RightSidebar.presenter';
import styles from './RightSidebar.styles.m.css';

interface IRightSidebarProps {
  onCloseViewSidebar(): void;
  openInfoViewSidebar(): void;
  openEditViewSidebar(): void;
  updateTemplateEvent(params: IUpdateEvent): void;
  updateSelectedEvent(params: IUpdateEvent): void;
  updateEventAfterUpdateParticipantStatusSuccess(event: IEventCalendar): void;
  addEventsAfterCreateRecurrenceSuccess(): void;
  addEventAfterCreateSuccess(newEvent: IEvent): void;
  deleteEventsAfterDeleteRecurrenceSuccess(id: string): void;
  deleteEventAfterDeleteSuccess(id: string): void;
  onEditEvent(
    event: IUpdateRecurrenceCalendarEventParams | IUpdateCalendarEventParams
  ): Promise<boolean>;
  jumpTo(date: Date): void;
  templateEvent: IEventCalendar | null;
  selectedEvent: IEventCalendar | undefined;
  activeSidebarView: ActiveSidebarView;
  isInitiator: boolean;
  accounts: ICalendarAuthAccount[];
  myEventStatusBySelectedEvent: ConfirmationType;
  isCompact?: boolean;
  isSideBarMinimized: boolean;
  minimizeSideBar(): void;
}

export const RightSidebar: FC<IRightSidebarProps> = observer((props) => {
  const presenter = useRightSidebarPresenter({
    onClose: props.onCloseViewSidebar,
    onInfo: props.openInfoViewSidebar,
    onEdit: props.openEditViewSidebar,
    updateEventAfterUpdateParticipantStatusSuccess:
      props.updateEventAfterUpdateParticipantStatusSuccess,
    onEditEvent: props.onEditEvent,
    addEventsAfterCreateRecurrenceSuccess: props.addEventsAfterCreateRecurrenceSuccess,
    addEventAfterCreateSuccess: props.addEventAfterCreateSuccess,
    deleteEventsAfterDeleteRecurrenceSuccess: props.deleteEventsAfterDeleteRecurrenceSuccess,
    deleteEventAfterDeleteSuccess: props.deleteEventAfterDeleteSuccess,
    activeView: props.activeSidebarView,
    selectedEvent: props.selectedEvent,
    templateEvent: props.templateEvent,
    accounts: props.accounts,
  });

  if (props.isSideBarMinimized) {
    return (
      <div className={styles.minimizedContainer}>
        {presenter.isCreate && props.templateEvent && (
          <Create
            selectedEvent={props.templateEvent}
            updateSelectedEvent={props.updateTemplateEvent}
            jumpTo={props.jumpTo}
            onCreate={presenter.onCreateEvent}
            onClose={presenter.onClose}
            minimizeSideBar={props.minimizeSideBar}
            participantsInfo={presenter.participantsInfo}
            isFormMinimized
          />
        )}
        {presenter.isEdit && presenter.selectedEvent && (
          <Edit
            selectedEvent={presenter.selectedEvent}
            updateSelectedEvent={props.updateSelectedEvent}
            jumpTo={props.jumpTo}
            onEdit={presenter.onEditEvent}
            participantsInfo={presenter.participantsInfo}
            onClose={presenter.onClose}
            minimizeSideBar={props.minimizeSideBar}
            isFormMinimized
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={classNames({
        [styles.wrapper]: props.isCompact,
      })}
    >
      <div
        className={classNames(styles.container, {
          [styles.roundedContainer]: props.isCompact,
        })}
      >
        <div className={styles.header}>
          {presenter.hasBackButton && (
            <IconButton
              className={styles.back}
              Icon={IcBackIcon}
              onClick={presenter.goBack}
              size={IconViewSizes.Big}
              iconSize={IconViewSizes.Medium}
            />
          )}
          <h3 className={styles.title}>{presenter.title}</h3>
          <div className={styles.actions}>
            {props.isInitiator && presenter.isInfo && (
              <>
                <IconButton
                  className={styles.close}
                  Icon={IcDeleteIcon}
                  onClick={presenter.onDelete}
                  size={IconViewSizes.Big}
                  iconSize={IconViewSizes.Medium}
                  dataTest="calendar-event-delete-button"
                />
                <IconButton
                  className={styles.close}
                  Icon={IcEditIcon}
                  onClick={presenter.onEdit}
                  size={IconViewSizes.Big}
                  iconSize={IconViewSizes.Medium}
                  dataTest="calendar-event-edit-button"
                />
              </>
            )}
            {props.isCompact && !presenter.isInfo && (
              <IconButton
                className={styles.close}
                Icon={IcArrowDown16Icon}
                onClick={props.minimizeSideBar}
                size={IconViewSizes.Medium}
              />
            )}
            <IconButton
              className={styles.close}
              Icon={IcCloseSIcon}
              onClick={presenter.onClose}
              size={IconViewSizes.Big}
              dataTest="calendar-event-close-button"
            />
          </div>
        </div>
        <div
          className={classNames(
            {
              [styles.body]: !props.isCompact,
              [styles.compactBody]: props.isCompact,
            },
            customScrollStyles.container
          )}
        >
          {presenter.isCreate && props.templateEvent && (
            <Create
              selectedEvent={props.templateEvent}
              updateSelectedEvent={props.updateTemplateEvent}
              jumpTo={props.jumpTo}
              onCreate={presenter.onCreateEvent}
              participantsInfo={presenter.participantsInfo}
            />
          )}
          {presenter.isEdit && presenter.selectedEvent && (
            <Edit
              selectedEvent={presenter.selectedEvent}
              updateSelectedEvent={props.updateSelectedEvent}
              jumpTo={props.jumpTo}
              onEdit={presenter.onEditEvent}
              participantsInfo={presenter.participantsInfo}
            />
          )}
          {presenter.isInfo && presenter.selectedEvent && (
            <Info
              selectedEvent={presenter.selectedEvent}
              myEventStatus={props.myEventStatusBySelectedEvent}
              updateStatus={presenter.updateParticipantStatus}
              participantsInfo={presenter.participantsInfo}
            />
          )}
        </div>
      </div>
    </div>
  );
});
